<script setup lang="ts">
import { useAuth0 } from '@auth0/auth0-vue'
import { useUserStore } from '../stores/user'
import { clearDevToken, getDevToken, useAuth } from '../composables/useAuth'
import { ref, onMounted, computed, watch, type Component } from 'vue'
import { createAccount, getAccounts } from '../services/api'
import { createStakeholder, createActivity, tagActivityToDeal, triggerExtraction, getWorkspaceDeals } from '../services/advisorWorkspaceApi'
import type { AdvisorActivityType, WorkspaceDeal } from '../types/workspace'
import { supplierNavItems, advisorNavItems, adminNavItems, type NavItem } from '../config/nav'
import { useRouter, useRoute } from 'vue-router'
import GlobalAiCompanion from '../components/GlobalAiCompanion.vue'
import NotificationBell from '../components/NotificationBell.vue'
import {
    LayoutDashboard, Building2, Package, Target, MessageSquare, Users,
    BookOpen, TrendingUp, Map, UserCog, Settings, FileText, LifeBuoy,
    Bot, GraduationCap, Calendar, CheckSquare, User, Landmark, DollarSign,
    Brain, Lightbulb, Microscope, Tag, Handshake, Ticket,
    Kanban, Sparkles, Megaphone, Search, Building, ContactRound,
    LogOut, ChevronDown, Plus, Zap, MessageCircle, Activity, UserPlus,
    Video, FolderOpen
} from 'lucide-vue-next'

const iconMap: Record<string, Component> = {
    LayoutDashboard, Building2, Package, Target, MessageSquare, Users,
    BookOpen, TrendingUp, Map, UserCog, Settings, FileText, LifeBuoy,
    Bot, GraduationCap, Calendar, CheckSquare, User, Landmark, DollarSign,
    Brain, Lightbulb, Microscope, Tag, Handshake, Ticket,
    Kanban, Sparkles, Megaphone, Search, Building, ContactRound,
    Zap, MessageCircle, Activity, UserPlus, Video, FolderOpen
}

const auth = useAuth0()
const { loginWithRedirect, user } = auth
const userStore = useUserStore()
const isAuthenticated = computed(() => auth.isAuthenticated.value || userStore.isAuthenticated)
const router = useRouter()
const isDark = ref(localStorage.getItem('theme') === 'dark' || document.documentElement.classList.contains('dark'))

const route = useRoute()
const searchQuery = ref('')
const isMobileMenuOpen = ref(false)

// Collapsible sidebar
const sidebarCollapsed = ref(localStorage.getItem('sidebarCollapsed') === 'true')
const sidebarHovered = ref(false)
const sidebarExpanded = computed(() => !sidebarCollapsed.value || sidebarHovered.value)

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value
  localStorage.setItem('sidebarCollapsed', String(sidebarCollapsed.value))
  if (!sidebarCollapsed.value) sidebarHovered.value = false
}

// Close mobile menu and dropdowns on route change
router.afterEach(() => {
  isMobileMenuOpen.value = false
  closeDropdowns()
})

const handleSearch = () => {
    if (!searchQuery.value.trim()) return

    // Determine context based on persona
    if (currentPersona.value === 'advisor') {
        router.push({ path: '/advisor/search', query: { q: searchQuery.value } })
        searchQuery.value = ''
    } else if (currentPersona.value === 'supplier') {
        router.push({ path: '/supplier/products', query: { search: searchQuery.value } })
    }
}

// Dark mode logic
const toggleDark = () => {
  isDark.value = !isDark.value
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

// Ensure dark class matches state on mount
onMounted(() => {
  if (isDark.value) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
})

// Persona Detection - determined by route and user role
const currentPersona = computed({
  get: () => {
    // Route-based detection takes priority
    if (route.path === '/' || route.path === '/login' || route.path === '/signup') return 'landing'
    if (route.path.startsWith('/supplier')) return 'supplier'
    if (route.path.startsWith('/advisor')) return 'advisor'
    if (route.path.startsWith('/admin')) return 'admin'

    // Fallback to user role
    if (userStore.isSupplier) return 'supplier'
    if (userStore.isAdvisor) return 'advisor'
    if (userStore.isAdmin) return 'admin'

    return 'landing'
  },
  set: (val) => {
    if (val === 'supplier') router.push('/supplier')
    else if (val === 'advisor') router.push('/advisor/workspace/deals')
    else if (val === 'admin') router.push('/admin')
    else router.push('/')
  }
})

// Sidebar Nav
const navItems = computed(() => {
  if (currentPersona.value === 'supplier') return supplierNavItems
  if (currentPersona.value === 'advisor') return advisorNavItems
  if (currentPersona.value === 'admin') return adminNavItems
  return []
})

// Group Nav Items by Section
const navSections = computed(() => {
    const sections: Record<string, typeof navItems.value> = {}
    navItems.value.forEach(item => {
        const key = item.section || 'Main'
        if (!sections[key]) sections[key] = []
        sections[key].push(item)
    })
    return sections
})

// Tier gating helpers
const tierRank: Record<string, number> = { FREE: 0, BASIC: 1, ENTERPRISE: 2 }

function isItemLocked(item: NavItem): boolean {
    if (!item.requiredTier) return false
    if (userStore.isImpersonating) return false
    if (currentPersona.value !== 'supplier') return false
    const current = userStore.supplierTier ?? 'FREE'
    return (tierRank[current] ?? 0) < (tierRank[item.requiredTier] ?? 0)
}

const showUpgradeModal = ref(false)
const lockedFeatureName = ref('')

function handleLockedClick(item: NavItem) {
    lockedFeatureName.value = item.label
    showUpgradeModal.value = true
}

// Header dropdown state (advisor only)
const supplierMenuOpen = ref(false)
const accountsMenuOpen = ref(false)

const settingsDropdownOpen = ref(false)
const userDropdownOpen = ref(false)

const settingsMenuItems = [
    { label: 'Profile', tab: 'profile' },
    { label: 'Defaults', tab: 'defaults' },
    { label: 'Today Page', tab: 'today' },
    { label: 'TSD Networks', tab: 'tsd' },
    { label: 'Suppliers', tab: 'suppliers' },
    { label: 'CRM Integrations', tab: 'integrations' },
    { label: 'Webhooks', tab: 'webhooks' },
    { label: 'Notifications', tab: 'notifications' },
    { label: 'Billing', tab: 'billing' },
]

// Close dropdowns on outside click
const closeDropdowns = () => {
    settingsDropdownOpen.value = false
    userDropdownOpen.value = false
    showPlusDropdown.value = false
}

const handleLogout = () => {
  const wasDevLogin = !!getDevToken()
  userStore.clearUser()
  clearDevToken()

  if (wasDevLogin && !auth.isAuthenticated.value) {
    // Dev-login user: no Auth0 session to destroy, just redirect locally
    router.push('/login')
  } else {
    // Auth0 SDK user: use Auth0's logout endpoint to clear the session
    auth.logout({ logoutParams: { returnTo: window.location.origin } })
  }
}

const handleLogin = () => {
  loginWithRedirect()
}

// Global Quick Add Account
const { getAccessTokenSilently } = useAuth()
const showGlobalQuickAdd = ref(false)
const quickAddName = ref('')
const quickAddContact = ref('')
const quickAddReferral = ref('')
const quickAddNote = ref('')
const savingGlobalLead = ref(false)
const showLeadToast = ref(false)
const lastLeadAccountId = ref<string | null>(null)

// Plus button dropdown
const showPlusDropdown = ref(false)

// Intake modal state
const showIntake = ref(false)
const intakeType = ref<AdvisorActivityType>('MEETING')
const intakeTitle = ref('')
const intakeContent = ref('')
const intakeAccountId = ref('')
const intakeNewAccountName = ref('')
const intakeDealId = ref('')
const intakeParticipants = ref('')
const intakeRunAI = ref(true)
const intakeAccounts = ref<{ id: string; name: string }[]>([])
const intakeAccountDeals = ref<WorkspaceDeal[]>([])
const savingIntake = ref(false)
const intakeError = ref('')

const openIntakeModal = async () => {
  showPlusDropdown.value = false
  showIntake.value = true
  intakeType.value = 'MEETING'
  intakeTitle.value = ''
  intakeContent.value = ''
  intakeAccountId.value = ''
  intakeNewAccountName.value = ''
  intakeDealId.value = ''
  intakeParticipants.value = ''
  intakeRunAI.value = true
  intakeError.value = ''
  try {
    const token = await getAccessTokenSilently()
    intakeAccounts.value = await getAccounts(token)
  } catch { /* best effort */ }
}

const loadAccountDeals = async (accountId: string) => {
  intakeAccountDeals.value = []
  intakeDealId.value = ''
  if (!accountId || accountId === '__new__') return
  try {
    const token = await getAccessTokenSilently()
    const allDeals = await getWorkspaceDeals(token)
    intakeAccountDeals.value = allDeals.filter((d: WorkspaceDeal) => d.account_id === accountId)
  } catch { /* best effort */ }
}

watch(() => intakeAccountId.value, (val) => loadAccountDeals(val))

const submitIntake = async () => {
  if (!intakeTitle.value || !intakeContent.value) return
  if (!intakeAccountId.value && !intakeNewAccountName.value) return
  savingIntake.value = true
  intakeError.value = ''
  try {
    const token = await getAccessTokenSilently()

    // 1. Create account if needed
    let accountId = intakeAccountId.value
    if (accountId === '__new__') {
      const account = await createAccount({ name: intakeNewAccountName.value }, token)
      accountId = account.id
    }

    // 2. Create the activity
    const isTranscript = intakeType.value === 'MEETING' || intakeType.value === 'CALL'
    const activity = await createActivity(accountId, {
      type: intakeType.value,
      date: new Date().toISOString(),
      title: intakeTitle.value,
      description: intakeContent.value,
      has_transcript: isTranscript,
      transcript_content: isTranscript ? intakeContent.value : undefined,
      participants: intakeParticipants.value ? intakeParticipants.value.split(',').map(s => s.trim()).filter(Boolean) : undefined,
    }, token)

    // 3. Tag to deal if selected
    if (intakeDealId.value && activity.id) {
      try { await tagActivityToDeal(activity.id, intakeDealId.value, token) } catch { /* best effort */ }
    }

    // 4. Trigger AI extraction if checked
    if (intakeRunAI.value && activity.id) {
      try { await triggerExtraction(activity.id, token) } catch { /* best effort */ }
    }

    // 5. Navigate to the activity detail
    showIntake.value = false
    router.push(`/advisor/workspace/accounts/${accountId}/activities/${activity.id}`)
  } catch (e: unknown) {
    intakeError.value = e instanceof Error ? e.message : 'Failed to save'
  } finally {
    savingIntake.value = false
  }
}

const saveGlobalLead = async () => {
  if (!quickAddName.value.trim()) return
  savingGlobalLead.value = true
  try {
    const token = await getAccessTokenSilently()
    const account = await createAccount({ name: quickAddName.value }, token)

    if (quickAddContact.value.trim() && account.id) {
      try { await createStakeholder(account.id, { name: quickAddContact.value }, token) } catch { /* best effort */ }
    }

    const noteParts: string[] = []
    if (quickAddReferral.value.trim()) noteParts.push(`Referral: ${quickAddReferral.value}`)
    if (quickAddNote.value.trim()) noteParts.push(quickAddNote.value)

    if (noteParts.length > 0 && account.id) {
      try {
        await createActivity(account.id, {
          type: 'NOTE',
          date: new Date().toISOString(),
          title: 'Lead captured',
          description: noteParts.join('\n'),
        }, token)
      } catch { /* best effort */ }
    }

    lastLeadAccountId.value = account.id
    showLeadToast.value = true
    setTimeout(() => { showLeadToast.value = false }, 8000)

    showGlobalQuickAdd.value = false
    quickAddName.value = ''
    quickAddContact.value = ''
    quickAddReferral.value = ''
    quickAddNote.value = ''
  } catch (e) { console.error(e) }
  finally { savingGlobalLead.value = false }
}
</script>

<template>
  <div class="min-h-screen bg-cc-deep transition-colors duration-300" @click="closeDropdowns">
    <GlobalAiCompanion />

    <!-- Mobile Backdrop -->
    <div
      v-if="isMobileMenuOpen"
      class="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm transition-opacity"
      @click="isMobileMenuOpen = false"
    ></div>

    <!-- Left Sidebar -->
    <aside
      class="bg-cc-surface border-r border-cc-border flex flex-col fixed inset-y-0 z-50 transition-all duration-300 ease-in-out md:translate-x-0"
      :class="[
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full',
        sidebarExpanded ? 'w-64' : 'w-16'
      ]"
      @mouseenter="sidebarCollapsed && (sidebarHovered = true)"
      @mouseleave="sidebarHovered = false"
    >

      <!-- Logo Area -->
      <div class="h-16 flex items-center border-b border-cc-border" :class="sidebarExpanded ? 'px-6' : 'px-0 justify-center'">
        <router-link to="/" class="flex items-center gap-2 transition opacity-80 hover:opacity-100">
          <img v-if="sidebarExpanded" :src="isDark ? '/assets/logo-white.png' : '/assets/logo-tcs-light.svg'" alt="The Channel Standard" class="h-8 w-auto" />
          <img v-else src="/assets/logo-mark.svg" alt="The Channel Standard" class="h-7 w-auto" />
        </router-link>
      </div>

      <!-- User Role Indicator -->
      <div v-if="userStore.user" class="border-b border-cc-border transition-all duration-200" :class="sidebarExpanded ? 'px-4 py-3' : 'px-2 py-3 flex justify-center'">
          <template v-if="sidebarExpanded">
            <div class="text-xs text-cc-text-tertiary mb-1">Logged in as</div>
            <div class="font-medium text-cc-text truncate">{{ userStore.user.name || userStore.user.email }}</div>
            <div class="text-xs text-cc-text-tertiary mt-1">
                {{ userStore.isAdvisor ? 'Advisor' : '' }}
                {{ userStore.isSupplier ? userStore.user.supplierEmployee?.supplier?.name : '' }}
                {{ userStore.isAdmin ? 'Administrator' : '' }}
            </div>
          </template>
          <template v-else>
            <div class="h-8 w-8 rounded-full bg-cc-primary-subtle text-cc-primary flex items-center justify-center font-bold text-xs border border-cc-border">
              {{ (userStore.user.name || userStore.user.email || '?').charAt(0) }}
            </div>
          </template>
      </div>

      <!-- Navigation Links -->
      <nav class="flex-1 overflow-y-auto transition-all duration-200" :class="sidebarExpanded ? 'p-4 space-y-6' : 'p-2 space-y-4'">

        <div v-for="(items, section) in navSections" :key="section">
            <h3 v-if="section !== 'Main' && sidebarExpanded" class="px-4 text-xs font-semibold text-cc-text-tertiary uppercase tracking-wider mb-2">
                {{ section }}
            </h3>
            <div v-else-if="section !== 'Main'" class="border-t border-cc-border mb-2"></div>
            <div class="space-y-1">
                <template v-for="item in items" :key="item.to">
                    <!-- Coming Soon items: greyed out, not clickable -->
                    <div
                        v-if="item.comingSoon"
                        class="flex items-center rounded-lg text-cc-text-tertiary cursor-default select-none"
                        :class="sidebarExpanded ? 'justify-between px-4 py-2 text-sm font-medium' : 'justify-center px-2 py-2'"
                        :title="!sidebarExpanded ? item.label + ' (Coming Soon)' : undefined"
                    >
                        <span class="flex items-center">
                            <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" :class="sidebarExpanded ? 'mr-3' : ''" />
                            <span v-if="sidebarExpanded">{{ item.label }}</span>
                        </span>
                        <span v-if="sidebarExpanded" class="text-[10px] uppercase tracking-wider text-cc-text-tertiary bg-cc-hover px-1.5 py-0.5 rounded">Soon</span>
                    </div>
                    <!-- Tier-locked items: greyed out with lock icon -->
                    <button
                        v-else-if="isItemLocked(item)"
                        @click="handleLockedClick(item)"
                        class="flex items-center w-full rounded-lg text-cc-text-tertiary hover:bg-cc-hover/50 transition-colors cursor-pointer"
                        :class="sidebarExpanded ? 'justify-between px-4 py-2 text-sm font-medium' : 'justify-center px-2 py-2'"
                        :title="!sidebarExpanded ? item.label + ' (Locked)' : undefined"
                    >
                        <span v-if="sidebarExpanded" class="flex items-center">
                            <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" class="mr-3" />
                            <span>{{ item.label }}</span>
                        </span>
                        <component v-else-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="20" class="opacity-40" />
                        <svg v-if="sidebarExpanded" class="w-3.5 h-3.5 text-cc-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </button>
                    <!-- Accounts with collapsible sub-items (advisor only) -->
                    <div v-else-if="item.label === 'Accounts' && currentPersona === 'advisor'">
                        <button
                            @click="$router.push(item.to); accountsMenuOpen = !accountsMenuOpen"
                            class="w-full flex items-center font-medium rounded-lg transition-colors group"
                            :class="[
                              sidebarExpanded ? 'px-4 py-2 text-sm justify-between' : 'justify-center px-2 py-2',
                              $route.path === item.to || $route.path.startsWith('/advisor/workspace/accounts') || $route.path.startsWith('/advisor/deal-intelligence') || $route.path.startsWith('/advisor/meetings') || $route.path.startsWith('/advisor/assets')
                                ? (sidebarExpanded ? 'bg-cc-hover text-cc-text' : 'bg-cc-hover text-cc-text')
                                : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                            ]"
                            :title="!sidebarExpanded ? item.label : undefined"
                        >
                            <span class="flex items-center">
                                <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" :class="sidebarExpanded ? 'mr-3' : ''" class="group-hover:scale-110 transition-transform" />
                                <span v-if="sidebarExpanded">{{ item.label }}</span>
                            </span>
                            <svg v-if="sidebarExpanded" :class="['w-3 h-3 transition-transform duration-200', accountsMenuOpen ? 'rotate-90' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                            </svg>
                        </button>
                        <div v-if="accountsMenuOpen && sidebarExpanded" class="ml-9 mt-1 space-y-0.5">
                            <router-link
                                to="/advisor/deal-intelligence"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/deal-intelligence') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Deal Intelligence
                            </router-link>
                            <router-link
                                to="/advisor/meetings"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/meetings') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Meetings
                            </router-link>
                            <span
                                class="block px-3 py-1.5 text-[12px] rounded text-cc-text-tertiary cursor-default flex items-center gap-1.5"
                            >
                                Assets
                                <span class="text-[10px] uppercase tracking-wider text-cc-text-tertiary bg-cc-hover px-1.5 py-0.5 rounded">Soon</span>
                            </span>
                        </div>
                    </div>
                    <!-- Supplier Directory with collapsible sub-items (advisor only) -->
                    <div v-else-if="item.label === 'Supplier Directory' && currentPersona === 'advisor'">
                        <button
                            @click="$router.push(item.to); supplierMenuOpen = !supplierMenuOpen"
                            class="w-full flex items-center font-medium rounded-lg transition-colors group"
                            :class="[
                              sidebarExpanded ? 'px-4 py-2 text-sm justify-between' : 'justify-center px-2 py-2',
                              $route.path === item.to || $route.path.startsWith('/advisor/suppliers') || $route.path.startsWith('/advisor/product-explorer')
                                ? (sidebarExpanded ? 'bg-cc-hover text-cc-text' : 'bg-cc-hover text-cc-text')
                                : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                            ]"
                            :title="!sidebarExpanded ? item.label : undefined"
                        >
                            <span class="flex items-center">
                                <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" :class="sidebarExpanded ? 'mr-3' : ''" class="group-hover:scale-110 transition-transform" />
                                <span v-if="sidebarExpanded">{{ item.label }}</span>
                            </span>
                            <svg v-if="sidebarExpanded" :class="['w-3 h-3 transition-transform duration-200', supplierMenuOpen ? 'rotate-90' : '']" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                            </svg>
                        </button>
                        <div v-if="supplierMenuOpen && sidebarExpanded" class="ml-9 mt-1 space-y-0.5">
                            <router-link
                                to="/advisor/suppliers"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path === '/advisor/suppliers' ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Product Directory
                            </router-link>
                            <router-link
                                to="/advisor/promotions"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/promotions') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Promotions
                            </router-link>
                            <router-link
                                to="/advisor/product-explorer"
                                class="block px-3 py-1.5 text-[12px] rounded transition-colors"
                                :class="$route.path.startsWith('/advisor/product-explorer') ? 'text-cc-text font-medium bg-cc-hover' : 'text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover'"
                            >
                                Product Explorer
                            </router-link>
                        </div>
                    </div>
                    <!-- Standard nav item -->
                    <router-link
                        v-else
                        :to="item.to"
                        class="flex items-center font-medium rounded-lg transition-colors group"
                        :class="[
                          sidebarExpanded ? 'px-4 py-2 text-sm' : 'justify-center px-2 py-2',
                          $route.path === item.to
                            ? (sidebarExpanded ? 'bg-cc-hover text-cc-text' : 'bg-cc-hover text-cc-text')
                            : 'text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text'
                        ]"
                        :title="!sidebarExpanded ? item.label : undefined"
                    >
                        <component v-if="item.icon && iconMap[item.icon]" :is="iconMap[item.icon]" :size="18" :class="sidebarExpanded ? 'mr-3' : ''" class="group-hover:scale-110 transition-transform" />
                        <span v-if="sidebarExpanded">{{ item.label }}</span>
                    </router-link>
                </template>
            </div>
        </div>

      </nav>

      <!-- Collapse Toggle -->
      <button
        @click="toggleSidebar"
        class="hidden md:flex items-center justify-center border-t border-cc-border py-3 text-cc-text-tertiary hover:text-cc-text hover:bg-cc-hover transition-colors"
        :title="sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      >
        <svg class="w-4 h-4 transition-transform duration-300" :class="sidebarCollapsed ? 'rotate-180' : ''" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
        </svg>
      </button>

      <!-- Tier Info -->
      <div v-if="userStore.user" class="border-t border-cc-border bg-cc-surface transition-all duration-200" :class="sidebarExpanded ? 'p-4' : 'p-2 text-center'">
         <template v-if="sidebarExpanded">
           <div class="text-xs text-cc-text-tertiary mb-1">Plan</div>
           <div class="text-sm font-medium" :class="userStore.advisorTier === 'PROFESSIONAL' || userStore.supplierTier === 'ENTERPRISE' ? 'text-cc-success' : 'text-cc-text-secondary'">
              {{ userStore.isAdvisor ? userStore.advisorTier : '' }}
              {{ userStore.isSupplier ? userStore.supplierTier : '' }}
              {{ userStore.isAdmin ? 'Admin' : '' }}
           </div>
         </template>
         <template v-else>
           <div class="w-6 h-6 rounded-full mx-auto flex items-center justify-center text-[10px] font-bold" :class="userStore.advisorTier === 'PROFESSIONAL' || userStore.supplierTier === 'ENTERPRISE' ? 'bg-cc-success/10 text-cc-success border border-cc-success/20' : 'bg-cc-hover text-cc-text-secondary border border-cc-border-med'"
             :title="userStore.isAdvisor ? (userStore.advisorTier ?? 'Free') : userStore.isSupplier ? (userStore.supplierTier ?? 'Free') : 'Admin'"
           >
             {{ userStore.isAdvisor ? (userStore.advisorTier || '').charAt(0) : userStore.isSupplier ? (userStore.supplierTier || '').charAt(0) : 'A' }}
           </div>
         </template>
      </div>

    </aside>

    <!-- Main Content Area -->
    <div class="flex-1 flex flex-col ml-0 min-w-0 transition-all duration-300" :class="sidebarExpanded ? 'md:ml-64' : 'md:ml-16'">

      <!-- Top Header -->
      <header class="bg-cc-surface border-b border-cc-border h-16 sticky top-0 z-40 flex items-center justify-between px-4 md:px-8 shadow-sm">

         <!-- Mobile Menu Button -->
         <button
           @click="isMobileMenuOpen = !isMobileMenuOpen"
           class="md:hidden text-cc-text-secondary hover:text-cc-text p-2 -ml-2 mr-2"
         >
           <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/></svg>
         </button>

         <!-- Search -->
         <div class="flex-1 max-w-2xl mr-4">
            <div class="relative">
              <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-cc-text-secondary">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
              </span>
              <input
                v-model="searchQuery"
                @keyup.enter="handleSearch"
                type="text"
                class="block w-full pl-10 pr-4 py-2 border border-cc-border-med rounded-lg leading-5 bg-cc-elevated text-cc-text placeholder-cc-text-tertiary focus:outline-none focus:ring-2 focus:ring-cc-primary focus:border-transparent sm:text-sm transition-colors"
                placeholder="Search..."
              />
            </div>
         </div>

         <!-- Right Actions -->
         <div class="flex items-center gap-4">

            <!-- Quick Add dropdown -->
            <div v-if="isAuthenticated && currentPersona === 'advisor'" class="relative">
              <button
                @click.stop="showPlusDropdown = !showPlusDropdown"
                title="Quick Add"
                style="background: var(--primary); color: white; width: 28px; height: 28px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer;"
              >
                <Plus :size="16" />
              </button>
              <div v-if="showPlusDropdown" class="absolute right-0 mt-2 w-52 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up">
                <button @click="showPlusDropdown = false; $router.push('/advisor/workspace/accounts/new')"
                  class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left">
                  <Building :size="16" />
                  New Account
                </button>
                <button @click="openIntakeModal()"
                  class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors text-left">
                  <FileText :size="16" />
                  Add Meeting / Note
                </button>
              </div>
            </div>

            <!-- Dark Mode Slider -->
            <button
              type="button"
              class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-cc-primary focus:ring-offset-2 focus:ring-offset-cc-surface"
              :class="isDark ? 'bg-cc-primary' : 'bg-cc-hover'"
              role="switch"
              :aria-checked="isDark"
              @click="toggleDark"
            >
              <span class="sr-only">Toggle Dark Mode</span>
              <span
                aria-hidden="true"
                class="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                :class="isDark ? 'translate-x-5' : 'translate-x-0'"
              >
                <span class="absolute inset-0 flex items-center justify-center" v-if="!isDark"><svg class="w-3 h-3 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd"/></svg></span>
                <span class="absolute inset-0 flex items-center justify-center" v-else><svg class="w-3 h-3 text-blue-300" fill="currentColor" viewBox="0 0 20 20"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg></span>
              </span>
            </button>

            <!-- Notifications -->
            <NotificationBell v-if="isAuthenticated" />

            <!-- Auth / Profile — Advisor gets cog + user dropdowns -->
            <template v-if="isAuthenticated && currentPersona === 'advisor'">
              <div class="flex items-center gap-2 pl-4 border-l border-cc-border-med h-8">

                <!-- Settings Cog Dropdown -->
                <div class="relative">
                  <button
                    @click.stop="settingsDropdownOpen = !settingsDropdownOpen; userDropdownOpen = false"
                    class="p-1.5 rounded-lg text-cc-text-secondary hover:text-cc-text hover:bg-cc-hover transition-colors"
                    title="Settings"
                  >
                    <Settings :size="18" />
                  </button>
                  <div
                    v-if="settingsDropdownOpen"
                    class="absolute right-0 mt-2 w-48 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up"
                  >
                    <router-link
                      v-for="item in settingsMenuItems"
                      :key="item.tab"
                      :to="`/advisor/settings?tab=${item.tab}`"
                      class="block px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="settingsDropdownOpen = false"
                    >
                      {{ item.label }}
                    </router-link>
                  </div>
                </div>

                <!-- User Avatar Dropdown -->
                <div class="relative">
                  <button
                    @click.stop="userDropdownOpen = !userDropdownOpen; settingsDropdownOpen = false"
                    class="flex items-center gap-2 p-1 rounded-lg hover:bg-cc-hover transition-colors"
                  >
                    <img v-if="(user as any)?.picture" :src="(user as any).picture" class="h-8 w-8 rounded-full border border-cc-border-med" />
                    <div v-else class="h-8 w-8 rounded-full bg-cc-primary-subtle text-cc-primary flex items-center justify-center font-bold text-sm border border-cc-border">
                      {{ (user?.name || userStore.user?.name || '?').charAt(0) }}
                    </div>
                    <ChevronDown :size="14" class="text-cc-text-tertiary hidden sm:block" />
                  </button>
                  <div
                    v-if="userDropdownOpen"
                    class="absolute right-0 mt-2 w-56 bg-cc-elevated rounded-lg border border-cc-border shadow-cc-dropdown z-50 py-1 animate-fade-in-up"
                  >
                    <div class="px-4 py-2.5 border-b border-cc-border">
                      <div class="text-sm font-medium text-cc-text truncate">{{ user?.name || userStore.user?.name }}</div>
                      <div class="text-xs text-cc-text-tertiary truncate">{{ user?.email || userStore.user?.email }}</div>
                    </div>
                    <router-link
                      to="/resources/docs"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="userDropdownOpen = false"
                    >
                      <FileText :size="16" />
                      Documentation
                    </router-link>
                    <router-link
                      to="/resources/support"
                      class="flex items-center gap-3 px-4 py-2 text-sm text-cc-text-secondary hover:bg-cc-hover hover:text-cc-text transition-colors"
                      @click="userDropdownOpen = false"
                    >
                      <LifeBuoy :size="16" />
                      Help & Support
                    </router-link>
                    <div class="border-t border-cc-border my-1"></div>
                    <button
                      @click="handleLogout"
                      class="flex items-center gap-3 w-full px-4 py-2 text-sm text-cc-danger hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut :size="16" />
                      Logout
                    </button>
                  </div>
                </div>

              </div>
            </template>

            <!-- Non-advisor auth (supplier/admin) — keep existing layout -->
            <div v-else-if="isAuthenticated" class="flex items-center gap-3 pl-4 border-l border-cc-border-med h-8">
               <router-link :to="currentPersona === 'supplier' ? '/supplier/settings' : '/admin'" class="flex items-center gap-3 hover:opacity-80 transition-opacity">
                   <div class="text-right hidden sm:block">
                     <div class="text-sm font-medium text-cc-text">{{ user?.name || userStore.user?.name }}</div>
                     <div class="text-xs text-cc-text-secondary truncate max-w-[150px]">{{ user?.email || userStore.user?.email }}</div>
                   </div>
                   <img v-if="(user as any)?.picture" :src="(user as any).picture" class="h-8 w-8 rounded-full border border-cc-border-med" />
                   <div v-else class="h-8 w-8 rounded-full bg-cc-primary-subtle text-cc-primary flex items-center justify-center font-bold border border-cc-border">
                     {{ (user?.name || userStore.user?.name || '?').charAt(0) }}
                   </div>
               </router-link>

               <button @click="handleLogout" class="text-xs text-cc-text-secondary hover:text-cc-danger ml-2">
                 Logout
               </button>
            </div>

            <button v-else @click="handleLogin" class="bg-cc-primary hover:bg-cc-primary-hover text-cc-btn-text px-4 py-2 rounded-cc-sm text-sm font-medium transition">
              Log In
            </button>

         </div>
      </header>

      <!-- Page Content -->
      <main class="flex-1 p-4 md:p-8 overflow-x-hidden">
        <slot />
      </main>

      <!-- Footer -->
      <footer class="p-6 text-center text-xs text-cc-text-tertiary">
        &copy; {{ new Date().getFullYear() }} Channel Companion LLC. All rights reserved. Channel Companion&trade; is a trademark of Channel Companion LLC.
      </footer>

    </div>

    <!-- Global Quick Add Modal -->
    <div v-if="showGlobalQuickAdd" class="fixed inset-0 z-50 flex items-center justify-center" style="background: rgba(0,0,0,0.4)">
      <div @click.stop style="background: var(--bg-elevated); border-radius: 8px; box-shadow: var(--shadow-dropdown); padding: 24px; width: 100%; max-width: 420px;">
        <h2 style="font-size: 17px; font-weight: 600; color: var(--text-primary); margin-bottom: 16px">Quick Add Account</h2>
        <div style="display: flex; flex-direction: column; gap: 10px;">
          <input v-model="quickAddName" placeholder="Company name *"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <input v-model="quickAddContact" placeholder="Contact name"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <input v-model="quickAddReferral" placeholder="Referral source"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none;" />
          <textarea v-model="quickAddNote" placeholder="Quick note..." rows="2"
            style="width: 100%; padding: 9px 14px; background: var(--bg-surface); border-radius: 6px; border: 1px solid var(--border-med); font-size: 13px; color: var(--text-primary); outline: none; resize: none;" />
        </div>
        <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px;">
          <button @click="showGlobalQuickAdd = false" style="padding: 9px 18px; font-size: 11px; font-weight: 500; color: var(--text-secondary); border-radius: 6px;">Cancel</button>
          <button @click="saveGlobalLead" :disabled="!quickAddName.trim() || savingGlobalLead"
            style="padding: 9px 18px; font-size: 11px; font-weight: 600; background: var(--primary); color: white; border-radius: 6px; cursor: pointer;"
            :style="{ opacity: !quickAddName.trim() || savingGlobalLead ? 0.5 : 1 }">
            {{ savingGlobalLead ? 'Saving...' : 'Save Lead' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lead created toast -->
    <div v-if="showLeadToast"
      class="fixed bottom-6 right-6 z-50 flex items-center gap-3"
      style="background: var(--bg-elevated); border-radius: 8px; box-shadow: var(--shadow-dropdown); padding: 14px 20px;">
      <span style="font-size: 13px; font-weight: 500; color: var(--success)">Lead created</span>
      <router-link v-if="lastLeadAccountId" :to="`/advisor/workspace/accounts/${lastLeadAccountId}`"
        style="font-size: 11px; font-weight: 600; color: var(--primary)">
        View →
      </router-link>
      <button @click="showLeadToast = false" style="color: var(--text-tertiary); font-size: 11px; margin-left: 8px">✕</button>
    </div>

    <!-- Upgrade Prompt Modal -->
    <Teleport to="body">
      <div v-if="showUpgradeModal" class="fixed inset-0 z-[100] flex items-center justify-center">
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm" @click="showUpgradeModal = false"></div>
        <div class="relative bg-cc-elevated rounded-xl shadow-cc-dropdown max-w-sm w-full mx-4 p-6 text-center">
          <div class="mx-auto w-12 h-12 rounded-full bg-cc-primary-subtle flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-cc-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 class="text-lg font-bold text-cc-text mb-2">Enterprise Feature</h3>
          <p class="text-sm text-cc-text-secondary mb-6">
            <span class="font-medium text-cc-text">{{ lockedFeatureName }}</span>
            requires an Enterprise plan. Upgrade to unlock full access to all features.
          </p>
          <div class="flex gap-3">
            <button
              @click="showUpgradeModal = false"
              class="flex-1 px-4 py-2 border border-cc-border-med text-cc-text-secondary rounded-cc-sm text-sm font-medium hover:bg-cc-hover transition"
            >
              Maybe Later
            </button>
            <router-link
              to="/supplier/settings"
              @click="showUpgradeModal = false"
              class="flex-1 px-4 py-2 bg-cc-primary text-cc-btn-text rounded-cc-sm text-sm font-medium hover:bg-cc-primary-hover transition text-center"
            >
              View Plans
            </router-link>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Intake Modal -->
    <Teleport to="body">
      <div v-if="showIntake" class="fixed inset-0 z-[100] flex items-center justify-center">
        <div class="absolute inset-0 bg-black/50" @click="showIntake = false"></div>
        <div class="relative bg-cc-elevated rounded-cc w-[640px] max-h-[80vh] overflow-y-auto" style="box-shadow: var(--shadow-dropdown)" @click.stop>
          <div class="p-5 border-b border-cc-border/20">
            <h2 class="text-lg font-bold text-cc-text">Add Intelligence</h2>
            <p class="text-sm text-cc-text-secondary mt-1">Paste a meeting transcript, note, or email. We'll figure out where it goes.</p>
          </div>

          <div class="p-5 space-y-4">
            <!-- Type selector -->
            <div class="flex gap-2">
              <button v-for="t in (['MEETING', 'NOTE', 'EMAIL', 'CALL'] as AdvisorActivityType[])" :key="t"
                @click="intakeType = t"
                :class="['px-3 py-1.5 rounded-cc-sm text-sm font-medium', intakeType === t ? 'bg-cc-primary text-cc-btn-text' : 'bg-cc-surface text-cc-text-secondary border border-cc-border']">
                {{ t.charAt(0) + t.slice(1).toLowerCase() }}
              </button>
            </div>

            <!-- Title -->
            <input v-model="intakeTitle" type="text" placeholder="Title (e.g., Discovery Call with Dr. Torres)"
              class="w-full px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />

            <!-- Content -->
            <textarea v-model="intakeContent" rows="8"
              :placeholder="intakeType === 'MEETING' || intakeType === 'CALL' ? 'Paste meeting transcript here...' : 'Type your note or paste email content...'"
              class="w-full px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm resize-y focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />

            <!-- Account matching -->
            <div>
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Account</label>
              <div class="flex gap-2 mt-1">
                <select v-model="intakeAccountId"
                  class="flex-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20">
                  <option value="">Select or create account...</option>
                  <option value="__new__">+ Create New Account</option>
                  <option v-for="a in intakeAccounts" :key="a.id" :value="a.id">{{ a.name }}</option>
                </select>
                <input v-if="intakeAccountId === '__new__'" v-model="intakeNewAccountName" placeholder="Account name"
                  class="flex-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />
              </div>
            </div>

            <!-- Deal matching -->
            <div v-if="intakeAccountId && intakeAccountId !== '__new__' && intakeAccountDeals.length > 0">
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Deal</label>
              <select v-model="intakeDealId"
                class="w-full mt-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20">
                <option value="">Don't link to a deal</option>
                <option v-for="d in intakeAccountDeals" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>

            <!-- Participants (for meetings/calls) -->
            <div v-if="intakeType === 'MEETING' || intakeType === 'CALL'">
              <label class="text-[11px] font-semibold uppercase tracking-wider text-cc-text-tertiary">Participants</label>
              <input v-model="intakeParticipants" type="text" placeholder="Names, comma separated"
                class="w-full mt-1 px-3 py-2 rounded-cc-sm border border-cc-border bg-cc-surface text-cc-text text-sm focus:outline-none focus:ring-2 focus:ring-cc-primary/20" />
            </div>

            <!-- Error message -->
            <p v-if="intakeError" class="text-sm text-cc-danger">{{ intakeError }}</p>
          </div>

          <div class="p-5 border-t border-cc-border/20 flex items-center justify-between">
            <label class="flex items-center gap-2 text-sm text-cc-text-secondary cursor-pointer">
              <input type="checkbox" v-model="intakeRunAI" class="rounded" />
              Run AI extraction
            </label>
            <div class="flex gap-2">
              <button @click="showIntake = false" class="px-4 py-2 text-sm text-cc-text-secondary hover:text-cc-text transition-colors">Cancel</button>
              <button @click="submitIntake" :disabled="!intakeTitle || !intakeContent || (!intakeAccountId && !intakeNewAccountName) || savingIntake"
                class="px-4 py-2 bg-cc-primary text-cc-btn-text rounded-cc-sm text-sm font-medium disabled:opacity-50 hover:bg-cc-primary-hover transition-colors">
                {{ savingIntake ? 'Saving...' : 'Save & Analyze' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
